import React, { useState, useEffect, useCallback } from 'react';
import { Download, Filter, Link as LinkIcon, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import SEOHead from '../components/SEOHead';
import { tariffsAPI, countriesAPI, exportAPI, tradeEventsAPI } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const Tariffs = () => {
  const [tariffs, setTariffs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCompareCountry, setSelectedCompareCountry] = useState(null);
  const [selectedSector, setSelectedSector] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tariffs'); // 'tariffs' or 'events'
  const [diffTariffs, setDiffTariffs] = useState([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (!selectedCountry || !selectedCompareCountry) {
      setDiffTariffs([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = {};
        if (selectedSector) params.sector = selectedSector.value;
        const res = await tariffsAPI.getAll(params);
        if (!cancelled) setDiffTariffs(res.data || []);
      } catch (error) {
        console.error('Error loading tariffs for visual diff:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCountry, selectedCompareCountry, selectedSector]);

  // Refetch tariffs when filters change
  const fetchFilteredTariffs = useCallback(async () => {
    try {
      const params = {};
      if (selectedCountry) params.country_id = selectedCountry.value;
      if (selectedSector) params.sector = selectedSector.value;
      const res = await tariffsAPI.getAll(params);
      setTariffs(res.data);
    } catch (error) {
      console.error('Error filtering tariffs:', error);
    }
  }, [selectedCountry, selectedSector]);

  useEffect(() => {
    fetchFilteredTariffs();
  }, [fetchFilteredTariffs]);

  const fetchInitialData = async () => {
    try {
      const [tariffsRes, countriesRes, categoriesRes] = await Promise.all([
        tariffsAPI.getAll(),
        countriesAPI.getAll(),
        tariffsAPI.getCategories(),
      ]);
      setTariffs(tariffsRes.data);
      setCountries(countriesRes.data.map(c => ({ value: c.id, label: c.name })));

      // Sectors from trade events
      const sectorOptions = categoriesRes.data.map(c => ({
        value: c.category,
        label: `${c.category} (${c.event_count} events)`,
        category: c.category,
      }));
      setSectors(sectorOptions);

      // Also fetch all trade events
      try {
        const eventsRes = await tradeEventsAPI.getAll({ limit: 100 });
        setAllEvents(eventsRes.data?.data || []);
      } catch (e) {
        // trade_events may not exist yet
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = {};
      if (selectedCountry) params.country_id = selectedCountry.value;

      const response = await exportAPI.tariffsCSV(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'tariffs_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting:', error);
    }
  };

  // Chart: avg tariff by sector from the tariff list
  const sectorAvgMap = {};
  tariffs.forEach(t => {
    const sector = t.category || 'Unknown';
    if (!sectorAvgMap[sector]) sectorAvgMap[sector] = { total: 0, count: 0 };
    sectorAvgMap[sector].total += parseFloat(t.tariff_rate || 0);
    sectorAvgMap[sector].count += 1;
  });
  const sectorChartData = Object.entries(sectorAvgMap).map(([sector, data]) => ({
    sector,
    'Avg Rate': (data.total / data.count).toFixed(1),
    'Events': data.count,
  })).sort((a, b) => parseFloat(b['Avg Rate']) - parseFloat(a['Avg Rate']));

  let visualDiffData = [];
  if (selectedCountry && selectedCompareCountry) {
    const a = selectedCountry.value;
    const b = selectedCompareCountry.value;
    const source = diffTariffs.length > 0 ? diffTariffs : tariffs;
    const agg = {};
    source.forEach((t) => {
      const sector = t.category || 'Unknown';
      if (!agg[sector]) agg[sector] = { a: [], b: [] };
      if (t.country_id === a) agg[sector].a.push(parseFloat(t.tariff_rate) || 0);
      if (t.country_id === b) agg[sector].b.push(parseFloat(t.tariff_rate) || 0);
    });
    visualDiffData = Object.entries(agg)
      .map(([sector, v]) => {
        const avgA = v.a.length ? v.a.reduce((s, x) => s + x, 0) / v.a.length : null;
        const avgB = v.b.length ? v.b.reduce((s, x) => s + x, 0) / v.b.length : null;
        if (avgA == null || avgB == null) return null;
        return { sector, spread: Number((avgA - avgB).toFixed(2)) };
      })
      .filter(Boolean)
      .slice(0, 14);
  }

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-primary-container border-t-transparent dark:border-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEOHead
        title="Tariff Rates & Trade Events Tracker | Import Duties & Export Bans"
        description="Track real-time tariff rates, import duties, export bans, anti-dumping measures, and trade events worldwide. AI-extracted data from global news sources covering tariff increases, decreases, and trade restrictions."
        path="/tariffs"
        keywords="tariff rates, import duties, export bans, trade events, anti-dumping, tariff increase, tariff decrease, customs duties, trade restrictions, import export tariffs"
      />
      <div className="mb-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="label-intel mb-2 text-slate-500 dark:text-outline">Tariff comparison tool</p>
            <h1 className="font-headline text-3xl font-bold text-slate-900 dark:text-on-surface">
              Tariff Comparison Tool
            </h1>
            <p className="mt-2 max-w-2xl text-slate-600 dark:text-on-surface-variant">
              Compare AI-extracted duties and trade events across countries and sectors—always verify against primary filings.
            </p>
          </div>
          <button
            type="button"
            onClick={handleExport}
            className="btn-intel-primary inline-flex shrink-0 items-center"
          >
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="mb-6 flex w-fit gap-1 rounded-stitch-xl bg-slate-100 p-1 dark:bg-surface-container-low">
        <button
          type="button"
          onClick={() => setActiveTab('tariffs')}
          className={`rounded-stitch-lg px-4 py-2 text-sm font-medium transition font-headline ${
            activeTab === 'tariffs'
              ? 'bg-white text-primary-700 shadow-sm dark:bg-surface-container-high dark:text-primary'
              : 'text-slate-600 hover:text-slate-900 dark:text-on-surface-variant dark:hover:text-on-surface'
          }`}
        >
          Tariff Events ({tariffs.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('events')}
          className={`rounded-stitch-lg px-4 py-2 text-sm font-medium transition font-headline ${
            activeTab === 'events'
              ? 'bg-white text-primary-700 shadow-sm dark:bg-surface-container-high dark:text-primary'
              : 'text-slate-600 hover:text-slate-900 dark:text-on-surface-variant dark:hover:text-on-surface'
          }`}
        >
          All Trade Events ({allEvents.length})
        </button>
      </div>

      {/* Filters — glass row */}
      <div className="glass-card mb-8 rounded-3xl border border-outline-variant/15 p-6 dark:border-outline-variant/20">
        <div className="mb-4 flex items-center gap-2">
          <Filter className="h-5 w-5 text-slate-500 dark:text-on-surface-variant" />
          <h3 className="font-headline font-medium text-slate-800 dark:text-on-surface">Scope filters</h3>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-on-surface-variant">
              Country A
            </label>
            <Select
              options={countries}
              value={selectedCountry}
              onChange={(val) => setSelectedCountry(val)}
              isClearable
              placeholder="Select a country..."
              classNamePrefix="select"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-on-surface-variant">
              Country B (compare)
            </label>
            <Select
              options={countries}
              value={selectedCompareCountry}
              onChange={(val) => setSelectedCompareCountry(val)}
              isClearable
              placeholder="Optional second country..."
              classNamePrefix="select"
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-on-surface-variant">
              Sector
            </label>
            <Select
              options={sectors}
              value={selectedSector}
              onChange={(val) => setSelectedSector(val)}
              isClearable
              placeholder="Select a sector..."
              classNamePrefix="select"
            />
          </div>
        </div>
      </div>

      {/* Main grid: sector chart + table + visual differential rail */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
      {sectorChartData.length > 0 && activeTab === 'tariffs' && (
        <div className="rounded-stitch-xl border border-slate-200/80 bg-white p-6 dark:border-outline-variant/15 dark:bg-surface-container lg:col-span-12">
          <h2 className="mb-4 font-headline text-xl font-semibold text-slate-900 dark:text-on-surface">
            Avg Tariff Rate by Sector (AI-Extracted)
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={sectorChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="sector" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" height={60} />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="Avg Rate" name="Avg Tariff %" fill="#2563eb" />
              <Bar dataKey="Events" name="Events" fill="#4edea3" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {/* ── Tab: Tariff Events ── */}
      {activeTab === 'tariffs' && (
        <div className="overflow-hidden rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container lg:col-span-8">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-slate-100 dark:bg-surface-container-low">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                    Country
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                    Target
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                    Sector
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                    Product
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                    Tariff Rate
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                    Impact
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                    Source
                  </th>
                </tr>
              </thead>
              <tbody>
                {tariffs.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-slate-500 dark:text-on-surface-variant">
                      {selectedCountry || selectedSector
                        ? 'No tariff events match the selected filters.'
                        : 'No tariff events found. Run the pipeline to extract tariff data from news.'}
                    </td>
                  </tr>
                ) : (
                  tariffs.slice(0, 100).map((tariff) => {
                    const eventTypeColors = {
                      tariff_increase: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
                      tariff_decrease: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
                    };
                    return (
                      <tr
                        key={tariff.id}
                        className="border-t border-slate-100 hover:bg-slate-50 dark:border-outline-variant/10 dark:hover:bg-surface-container-low/60"
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-slate-900 dark:text-on-surface">
                          {tariff.country_name}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600 dark:text-on-surface-variant">
                          {tariff.target_country_name || tariff.target_country || '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600 dark:text-on-surface-variant">
                          {tariff.category || '—'}
                        </td>
                        <td
                          className="max-w-[150px] truncate px-4 py-3 text-sm text-slate-600 dark:text-on-surface-variant"
                          title={tariff.product_description}
                        >
                          {tariff.product_description || '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm font-bold text-slate-900 dark:text-on-surface">
                          {parseFloat(tariff.tariff_rate).toFixed(1)}%
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${eventTypeColors[tariff.tariff_type] || 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'}`}>
                            {(tariff.tariff_type || '').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {tariff.impact_score != null ? (
                            <span
                              className={`font-medium ${
                                tariff.impact_score >= 60
                                  ? 'text-red-600 dark:text-error'
                                  : tariff.impact_score >= 40
                                    ? 'text-amber-600 dark:text-primary-fixed-dim'
                                    : 'text-emerald-600 dark:text-tertiary'
                              }`}
                            >
                              {tariff.impact_score}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-500 dark:text-on-surface-variant">
                          {tariff.effective_date ? new Date(tariff.effective_date).toLocaleDateString() : '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm">
                          {tariff.source_url ? (
                            <a
                              href={tariff.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block max-w-[120px] truncate text-primary-600 hover:underline dark:text-primary"
                              title={tariff.source_name}
                            >
                              {tariff.source_name || 'Source'}
                            </a>
                          ) : (
                            <span className="text-slate-400 dark:text-outline">{tariff.source_name || '—'}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Tab: All Trade Events ── */}
      {activeTab === 'events' && (
        <div className="overflow-hidden rounded-stitch-xl border border-slate-200/80 bg-white dark:border-outline-variant/15 dark:bg-surface-container lg:col-span-8">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-slate-100 dark:bg-surface-container-low">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                    Event Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                    Imposing
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                    Target
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                    Sector
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                    Product
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                    Tariff %
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                    Impact
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                    Confidence
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-on-surface-variant">
                    Source
                  </th>
                </tr>
              </thead>
              <tbody>
                {allEvents.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-slate-500 dark:text-on-surface-variant">
                      No AI-extracted trade events yet. Run the pipeline to populate.
                    </td>
                  </tr>
                ) : (
                  allEvents.map((ev) => {
                    const eventTypeColors = {
                      tariff_increase: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
                      tariff_decrease: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
                      trade_deal: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
                      sanction: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
                      import_ban: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
                      export_ban: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
                      dispute: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
                      subsidy: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
                      anti_dumping: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
                      quota: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
                    };
                    return (
                      <tr
                        key={ev.id}
                        className="border-t border-slate-100 hover:bg-slate-50 dark:border-outline-variant/10 dark:hover:bg-surface-container-low/60"
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${eventTypeColors[ev.event_type] || 'bg-slate-100 text-slate-700 dark:bg-secondary-container dark:text-on-secondary-container'}`}>
                            {ev.event_type?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-slate-900 dark:text-on-surface">
                          {ev.imposing_country || '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600 dark:text-on-surface-variant">
                          {ev.target_country || '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600 dark:text-on-surface-variant">
                          {ev.sector || '—'}
                        </td>
                        <td
                          className="max-w-[150px] truncate px-4 py-3 text-sm text-slate-600 dark:text-on-surface-variant"
                          title={ev.product}
                        >
                          {ev.product || '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-slate-900 dark:text-on-surface">
                          {ev.tariff_rate_percent ? `${ev.tariff_rate_percent}%` : '—'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {ev.impact_score != null ? (
                            <span
                              className={`font-medium ${
                                ev.impact_score >= 60
                                  ? 'text-red-600 dark:text-error'
                                  : ev.impact_score >= 40
                                    ? 'text-amber-600 dark:text-primary-fixed-dim'
                                    : 'text-emerald-600 dark:text-tertiary'
                              }`}
                            >
                              {ev.impact_score}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-500 dark:text-on-surface-variant">
                          {ev.confidence_score != null ? (parseFloat(ev.confidence_score) * 100).toFixed(0) + '%' : '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-3 text-sm">
                          {ev.source_url ? (
                            <a
                              href={ev.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block max-w-[120px] truncate text-primary-600 hover:underline dark:text-primary"
                              title={ev.source_name}
                            >
                              {ev.source_name || 'Source'}
                            </a>
                          ) : (
                            <span className="text-slate-400 dark:text-outline">{ev.source_name || '—'}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <aside className="space-y-6 lg:col-span-4">
        <div className="rounded-3xl border border-outline-variant/15 bg-surface-container-low p-5 dark:bg-surface-container">
          <h2 className="font-headline text-lg font-semibold text-slate-900 dark:text-on-surface">Visual differential</h2>
          <p className="mt-1 text-xs text-slate-500 dark:text-on-surface-variant">
            Average reported tariff by sector for Country A minus Country B using the filtered tariff list (pp difference).
          </p>
          {selectedCountry && selectedCompareCountry ? (
            visualDiffData.length > 0 ? (
              <div className="mt-4 h-[280px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={visualDiffData} layout="vertical" margin={{ left: 8, right: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="sector" type="category" width={100} tick={{ fontSize: 10 }} />
                    <Tooltip
                      formatter={(value, name) => {
                        if (name === 'spread') return [`${value}%`, 'A − B'];
                        return [value, name];
                      }}
                      labelFormatter={() => ''}
                    />
                    <Bar dataKey="spread" name="spread" fill="#2563eb" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="mt-4 text-sm text-slate-500 dark:text-on-surface-variant">
                No overlapping sector rows for these countries in the current filter. Broaden filters or ingest more tariff events.
              </p>
            )
          ) : (
            <p className="mt-4 text-sm text-slate-500 dark:text-on-surface-variant">
              Select Country A and Country B to plot a sector spread chart from the visible tariff rows.
            </p>
          )}
        </div>

        <div className="rounded-3xl border border-outline-variant/15 bg-surface-container-low p-5 dark:bg-surface-container">
          <div className="flex items-center gap-2 text-primary-600 dark:text-primary">
            <LinkIcon className="h-5 w-5" aria-hidden />
            <h2 className="font-headline text-lg font-semibold text-slate-900 dark:text-on-surface">Related legislation</h2>
          </div>
          <ul className="mt-3 space-y-2 text-sm text-slate-600 dark:text-on-surface-variant">
            <li>
              <Link to="/legislation" className="font-medium text-primary-600 hover:underline dark:text-primary">
                Legislation tracker
              </Link>
            </li>
            <li>
              <Link to="/legislation#sanctions" className="hover:underline">
                Sanctions &amp; export controls (anchor)
              </Link>
            </li>
            <li>
              <Link to="/insights/tariffs-vs-legislation" className="hover:underline">
                Tariffs vs. legislation guide
              </Link>
            </li>
          </ul>
        </div>

        <div className="rounded-3xl border border-dashed border-primary/30 bg-primary-container/10 p-5 dark:bg-primary-container/5">
          <div className="flex items-center gap-2 text-primary-700 dark:text-primary">
            <Sparkles className="h-5 w-5" aria-hidden />
            <h2 className="font-headline text-sm font-semibold uppercase tracking-wide">AI context</h2>
          </div>
          <p className="mt-2 text-sm text-slate-600 dark:text-on-surface-variant">
            Cross-check extracted rates with filings and customs notices. Our methodology explains confidence and known failure modes.
          </p>
          <Link
            to="/insights/methodology"
            className="mt-3 inline-flex text-sm font-semibold text-primary-600 dark:text-primary hover:underline"
          >
            Read methodology →
          </Link>
        </div>
      </aside>
      </div>
    </div>
  );
};

export default Tariffs;
